import { useFormContext } from "react-hook-form";
import Image from "next/image";
import clsx from "clsx";
import { useDispatch, useSelector } from "react-redux";

import { Button, Card, Skeleton } from "@chef/components";
import { Cooking } from "@chef/icons/symbol";
import { PreferencesDetails } from "@chef/smart-components";
import { findFeaturedPortraitImage } from "@chef/state-management/helpers";
import {
  selectSignupWeekAndYear,
  setPersonalizedRecipesModal,
  usePreferencesQuery,
} from "@chef/state-management";
import {
  BASE_64_1X1_GREY,
  getDishCount,
  PREFERENCE_IDS,
  PreviewRecipe,
} from "@chef/constants";
import { ChevronRight, Loading } from "@chef/icons/small";
import { isFixedPreference } from "@chef/state-management/helpers";
import { isEmptyArray, isNotEmptyArray } from "@chef/utils/array";

import { usePreviewRecipes } from "../hooks/usePreviewRecipes";

import { intl } from "./MealboxSummary.Intl";

interface MealboxSummaryProps {
  isSubmitting?: boolean;
}
interface PreselectedRecipeProps {
  recipe: PreviewRecipe;
  hiddenMobile?: boolean;
}

const PreselectedRecipe = ({
  recipe,
  hiddenMobile,
}: PreselectedRecipeProps) => {
  return (
    <li
      className={clsx(
        hiddenMobile && "hidden md:block",
        "flex items-center gap-3",
      )}
    >
      <div className="relative w-18 md:w-20 h-28 md:h-32 shrink-0">
        <Image
          src={findFeaturedPortraitImage(recipe)?.url || BASE_64_1X1_GREY}
          fill
          alt={recipe.recipeName}
          className="object-cover rounded"
          sizes="(max-width: 768px) 80px, 128px"
        />
      </div>
    </li>
  );
};

export const MealboxSummary = ({ isSubmitting }: MealboxSummaryProps) => {
  const dispatch = useDispatch();

  const { week, year } = useSelector(selectSignupWeekAndYear);

  const methods = useFormContext();
  const { data: preferencesQuery } = usePreferencesQuery();

  const [tastePreferenceIds, conceptPreferenceIds, portions] = methods.watch([
    "tastePreferenceIds",
    "conceptPreferenceIds",
    "portions",
  ]);

  const isFixed = conceptPreferenceIds.includes(isFixedPreference);

  const shouldShowChefsChoicePreference =
    !isFixed && isEmptyArray(conceptPreferenceIds);

  const conceptPreferences =
    preferencesQuery?.preferences.filter((preference) =>
      conceptPreferenceIds.includes(preference.preferenceId),
    ) || [];

  const tastePreferences =
    preferencesQuery?.preferences.filter((preference) =>
      tastePreferenceIds.includes(preference.preferenceId),
    ) || [];

  const { recipes, isLoading } = usePreviewRecipes({
    week,
    year,
    portions,
    conceptPreferenceIds,
    tastePreferenceIds,
  });

  const hasSelectedPreferences =
    isNotEmptyArray(conceptPreferences) || isNotEmptyArray(tastePreferences);

  const dishCount = getDishCount({ week, year, portions });

  return (
    <div className="mx-0 bg-grey-2 page-padding grow md:bg-white">
      <div className="page-max-width page-grid">
        <Card className="px-6 pb-8 mb-8 mt-28 md:mt-0 col-span-full lg:col-span-6 xl:col-span-10 xl:col-start-2 lg:col-start-2 md:shadow-none">
          <div className="relative flex flex-col items-center gap-6 bg-white md:pb-0 md:pt-4 pt-14">
            <div className="absolute border-4 border-white rounded-full -top-16 bg-grey-3 md:hidden">
              <Cooking width={96} height={96} className="p-4" />
            </div>

            <div className="text-center">
              <strong className="mb-1 text-sm text-secondary">
                {intl.WELL_DONE}
              </strong>
              <h1 className="text-lg text-center">
                <strong>{intl.DELICIOUS_MEALKIT}</strong>
              </h1>
            </div>

            <ul className="grid grid-cols-4 gap-2 md:grid-cols-5">
              {isLoading
                ? [...Array(5)].map((_, i) => (
                    <li
                      key={`mealbox-summary-skeleton-${i}`}
                      className={clsx(
                        "w-18 h-28 md:w-20 md:h-32",
                        i === 4 && "hidden md:block",
                      )}
                    >
                      <Skeleton
                        className="flex items-center justify-center"
                        height="100%"
                      >
                        <Loading className="animate-spin" />
                      </Skeleton>
                    </li>
                  ))
                : recipes
                    ?.slice(0, 5)
                    .map((recipe, index) => (
                      <PreselectedRecipe
                        key={recipe.recipeId}
                        recipe={recipe}
                        hiddenMobile={index === 4}
                      />
                    ))}
            </ul>

            {hasSelectedPreferences && (
              <PreferencesDetails
                conceptPreferences={conceptPreferences}
                tastePreferences={tastePreferences}
                className="w-full"
              />
            )}

            <div className="text-center">
              <p>
                <strong>
                  {intl.CHOOSE_BETWEEN} {dishCount} {intl.DISHES_PER_WEEK}
                </strong>
              </p>
              <p>
                <button
                  className="underline underline-offset-4 text-primary"
                  type="button"
                  onClick={() =>
                    dispatch(
                      setPersonalizedRecipesModal({
                        open: true,
                        conceptPreferenceIds: shouldShowChefsChoicePreference
                          ? [PREFERENCE_IDS.CHEF_CHOICE]
                          : conceptPreferenceIds,
                        tastePreferenceIds,
                      }),
                    )
                  }
                >
                  <strong>{intl.SHOW_PERSONALIZED_MENU}</strong>
                </button>
              </p>
            </div>

            <Button
              type="submit"
              primary
              className="w-full md:w-3/4"
              loading={isSubmitting}
              id="go-forward-button"
            >
              <div className="flex items-center justify-center gap-2">
                <span>{intl.CONTINUE}</span>
                <ChevronRight width={12} height={12} />
              </div>
            </Button>

            <p className="text-sm text-center text-grey-1 md:hidden">
              {intl.YOU_CAN_CHANGE_ANYTIME}
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
};
